import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Task, TaskAttachment } from '@/types';
import CategoryInput from '@/shared/ui/inputs/CategoryInput';
import './styles.css';

type TaskFormData = Omit<Task, '_id' | 'createdAt' | 'updatedAt'>;

const initialFormData: TaskFormData = {
  title: '',
  content: '',
  category: '',
  scoringMethod: 'numerical',
  scoringScale: {
    min: 0,
    max: 3
  },
  scoringRules: '',
  solution: ''
};

export default function TaskForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TaskFormData>(initialFormData);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TaskAttachment[]>([]);
  const [loading, setLoading] = useState(false);

  const isEditMode = Boolean(id);

  useEffect(() => {
    if (isEditMode) {
      loadTask();
    }
  }, [id]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const task = await api.tasks.getById(id!);
      setFormData({
        title: task.title,
        content: task.content,
        category: task.category,
        scoringMethod: task.scoringMethod,
        scoringScale: task.scoringScale,
        scoringRules: task.scoringRules || '',
        solution: task.solution || ''
      });
      if (task.attachments && task.attachments.length > 0) {
        setExistingAttachments(task.attachments);
      }
    } catch (err) {
      toast.error('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Create FormData for file uploads
      const formDataObj = new FormData();
      
      // Add task data
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'scoringScale' && value) {
          formDataObj.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null) {
          formDataObj.append(key, String(value));
        }
      });
      
      // Add existing attachments to keep
      if (isEditMode && existingAttachments.length > 0) {
        formDataObj.append('existingAttachments', JSON.stringify(existingAttachments.map(att => att._id)));
      }
      
      // Add new attachments
      attachments.forEach(file => {
        formDataObj.append('attachments', file);
      });
      
      if (isEditMode) {
        await api.tasks.update(id!, formDataObj);
        toast.success('Task updated successfully!');
      } else {
        await api.tasks.create(formDataObj);
        toast.success('Task created successfully!');
      }
      navigate('/tasks');
    } catch (err) {
      const errorMessage = (err as ApiError).message || `Failed to ${isEditMode ? 'update' : 'create'} task`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'min' || name === 'max') {
      setFormData(prev => ({
        ...prev,
        scoringScale: {
          ...prev.scoringScale!,
          [name]: parseInt(value)
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const handleRemoveExistingAttachment = (attachmentId: string) => {
    setExistingAttachments(prev => prev.filter(att => att._id !== attachmentId));
  };

  const handleCategoryChange = (value: string) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  if (loading && isEditMode) return <div>Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>{isEditMode ? 'Edit Task' : 'Create New Task'}</h1>
      </div>
      <div className="page-content">
        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter a concise title for the task"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Content</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter the detailed task content"
              required
            />
          </div>

          <CategoryInput
            value={formData.category}
            onChange={handleCategoryChange}
            required
          />

          <div className="form-group">
            <label htmlFor="scoringMethod">Scoring Method</label>
            <select
              id="scoringMethod"
              name="scoringMethod"
              value={formData.scoringMethod}
              onChange={handleChange}
            >
              <option value="numerical">Numerical</option>
              <option value="binary">Binary (Pass/Fail)</option>
            </select>
          </div>

          {formData.scoringMethod === 'numerical' && (
            <div className="form-group scoring-scale">
              <h3>Scoring Scale</h3>
              <div className="scale-inputs">
                <div>
                  <label htmlFor="min">Minimum</label>
                  <input
                    type="number"
                    id="min"
                    name="min"
                    value={formData.scoringScale?.min}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="max">Maximum</label>
                  <input
                    type="number"
                    id="max"
                    name="max"
                    value={formData.scoringScale?.max}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="scoringRules">Scoring Rules</label>
            <textarea
              id="scoringRules"
              name="scoringRules"
              value={formData.scoringRules}
              onChange={handleChange}
              placeholder="Enter the rules for scoring this task (optional)"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="solution">Solution</label>
            <textarea
              id="solution"
              name="solution"
              value={formData.solution}
              onChange={handleChange}
              placeholder="Enter the solution or correct answer for this task (optional)"
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="attachments">Attachments</label>
            <input
              type="file"
              id="attachments"
              name="attachments"
              multiple
              onChange={handleFileChange}
            />
            <div className="file-info">
              {attachments.length > 0 && (
                <div className="new-attachments">
                  <h4>Files to upload:</h4>
                  <ul>
                    {attachments.map((file, index) => (
                      <li key={`new-${index}`}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          
          {existingAttachments.length > 0 && (
            <div className="form-group existing-attachments">
              <h4>Existing Attachments</h4>
              <ul>
                {existingAttachments.map((attachment) => (
                  <li key={attachment._id}>
                    <span>{attachment.originalname}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingAttachment(attachment._id!)}
                      className="remove-attachment"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/tasks')} className="button">
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 